import { Formik } from 'formik';
import React, { useRef } from 'react';
import * as Yup from 'yup';
import {
  DisciplineSelect,
  Form,
  JobTitleSelect,
  LocationSelect,
  MemberCertificationMultiSelect,
  MemberLevelSelect,
  MemberSelect,
  MemberTagMultiSelect,
  PracticeSelect,
  SkillMultiSelect,
} from '~/components';
import { BulkActionEditControl, NO_CHANGE, CLEAR, SET, BulkActionModal } from '~/components/bulk-action';
import FixedDropdown from '~/components/FixedDropdown';
import FixedSingleSelect from '~/components/FixedSingleSelect';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import MemberDetails from './MemberDetails';
import HolidayScheduleSelect from '~/components/HolidayScheduleSelect';
import SecurityRoleSelect from '~/components/SecurityRoleSelect';
import LoginEnabledSelect from './LoginEnabledSelect';
import MemberTypeSelect from './MemberTypeSelect';

const validationSchema = Yup.object().shape({
  jobTitle: Yup.object()
    .nullable()
    .when('jobTitleAction', { is: SET, then: (schema) => schema.required('Please select a job title.') }),
  memberLevel: Yup.object()
    .nullable()
    .when('memberLevelAction', { is: SET, then: (schema) => schema.required('Please select a member level.') }),
  practice: Yup.object()
    .nullable()
    .when('practiceAction', { is: SET, then: (schema) => schema.required('Please select a practice.') }),
  discipline: Yup.object()
    .nullable()
    .when('disciplineAction', { is: SET, then: (schema) => schema.required('Please select a discipline.') }),
  manager: Yup.object()
    .nullable()
    .when('managerAction', { is: SET, then: (schema) => schema.required('Please select a manager.') }),
  location: Yup.object()
    .nullable()
    .when('locationAction', { is: SET, then: (schema) => schema.required('Please select a location.') }),
  holidaySchedule: Yup.object()
    .nullable()
    .when('holidayScheduleAction', { is: SET, then: (schema) => schema.required('Please select a holiday schedule.') }),
  securityRole: Yup.object()
    .nullable()
    .when('securityRoleAction', { is: SET, then: (schema) => schema.required('Please select a security role.') }),
  tags: Yup.array()
    .nullable()
    .when('tagsAction', { is: SET, then: (schema) => schema.min(1, 'Please select at least one tag.') }),
  skills: Yup.array()
    .nullable()
    .when('skillsAction', { is: SET, then: (schema) => schema.min(1, 'Please select at least one skill.') }),
  certifications: Yup.array()
    .nullable()
    .when('certificationsAction', {
      is: SET,
      then: (schema) => schema.min(1, 'Please select at least one certification.'),
    }),
  loginEnabled: Yup.string()
    .nullable()
    .when('loginEnabledAction', { is: SET, then: (schema) => schema.required('Please select a value.') }),
  memberType: Yup.string()
    .nullable()
    .when('memberTypeAction', { is: SET, then: (schema) => schema.required('Please select a member type.') }),
});

const validate = (values) => {
  const errors = {};

  const hasAtLeastOneChange =
    values.jobTitleAction !== NO_CHANGE ||
    values.memberLevelAction !== NO_CHANGE ||
    values.practiceAction !== NO_CHANGE ||
    values.disciplineAction !== NO_CHANGE ||
    values.managerAction !== NO_CHANGE ||
    values.locationAction !== NO_CHANGE ||
    values.holidayScheduleAction !== NO_CHANGE ||
    values.securityRoleAction !== NO_CHANGE ||
    values.tagsAction !== NO_CHANGE ||
    values.skillsAction !== NO_CHANGE ||
    values.certificationsAction !== NO_CHANGE ||
    values.loginEnabledAction !== NO_CHANGE ||
    values.memberTypeAction !== NO_CHANGE;

  if (!hasAtLeastOneChange) {
    const errorMessage = 'At least one field is required.';
    errors.jobTitleAction = errorMessage;
    errors.memberLevelAction = errorMessage;
    errors.practiceAction = errorMessage;
    errors.disciplineAction = errorMessage;
    errors.managerAction = errorMessage;
    errors.locationAction = errorMessage;
    errors.holidayScheduleAction = errorMessage;
    errors.securityRoleAction = errorMessage;
    errors.tagsAction = errorMessage;
    errors.skillsAction = errorMessage;
    errors.certificationsAction = errorMessage;
    errors.loginEnabledAction = errorMessage;
    errors.memberTypeAction = errorMessage;
  }

  return errors;
};

function BulkEditForm({ features }) {
  return (
    <Form style={{ paddingBottom: '2rem' }}>
      <p style={{ marginBottom: '1.5rem' }}>Select the fields that you would like to bulk update.</p>

      <BulkActionEditControl
        label="Login Enabled"
        actionName="loginEnabledAction"
        valueName="loginEnabled"
        allowClear={false}>
        {(props) => <LoginEnabledSelect {...props} component={FixedSingleSelect} />}
      </BulkActionEditControl>

      <BulkActionEditControl label="Manager" actionName="managerAction" valueName="manager">
        {(props) => <MemberSelect {...props} placeholder="Manager" component={FixedDropdown} />}
      </BulkActionEditControl>

      {features.practices && (
        <BulkActionEditControl label="Practice" actionName="practiceAction" valueName="practice">
          {(props) => <PracticeSelect {...props} placeholder="Practice" allowNew={true} component={FixedDropdown} />}
        </BulkActionEditControl>
      )}

      {features.disciplines && (
        <BulkActionEditControl label="Discipline" actionName="disciplineAction" valueName="discipline">
          {(props) => (
            <DisciplineSelect {...props} placeholder="Discipline" allowNew={true} component={FixedDropdown} />
          )}
        </BulkActionEditControl>
      )}

      <BulkActionEditControl label="Job Title" actionName="jobTitleAction" valueName="jobTitle">
        {(props) => <JobTitleSelect {...props} placeholder="Job Title" allowNew={true} component={FixedDropdown} />}
      </BulkActionEditControl>

      <BulkActionEditControl label="Level" actionName="memberLevelAction" valueName="memberLevel">
        {(props) => <MemberLevelSelect {...props} placeholder="Level" allowNew={true} component={FixedDropdown} />}
      </BulkActionEditControl>

      <BulkActionEditControl label="Tags" actionName="tagsAction" valueName="tags" emptyValue={[]} multiSelect>
        {(props) => <MemberTagMultiSelect {...props} placeholder="Tags" allowNew={true} component={FixedDropdown} />}
      </BulkActionEditControl>

      <BulkActionEditControl label="Skills" actionName="skillsAction" valueName="skills" emptyValue={[]} multiSelect>
        {(props) => <SkillMultiSelect {...props} placeholder="Skills" allowNew={true} component={FixedDropdown} />}
      </BulkActionEditControl>

      <BulkActionEditControl
        label="Certifications"
        actionName="certificationsAction"
        valueName="certifications"
        emptyValue={[]}
        multiSelect>
        {(props) => (
          <MemberCertificationMultiSelect
            {...props}
            placeholder="Certifications"
            allowNew={true}
            component={FixedDropdown}
          />
        )}
      </BulkActionEditControl>

      <BulkActionEditControl label="Holiday Schedule" actionName="holidayScheduleAction" valueName="holidaySchedule">
        {(props) => (
          <HolidayScheduleSelect
            name={props.name}
            value={props.value?.id}
            onChange={(e) => props.onChange({ target: { value: e.target.value ? { id: e.target.value } : null } })}
            disabled={props.disabled}
            placeholder="Holiday Schedule"
            component={FixedSingleSelect}
          />
        )}
      </BulkActionEditControl>

      <BulkActionEditControl
        label="Member Type"
        actionName="memberTypeAction"
        valueName="memberType"
        allowClear={false}>
        {(props) => <MemberTypeSelect {...props} component={FixedSingleSelect} />}
      </BulkActionEditControl>

      <BulkActionEditControl label="Location" actionName="locationAction" valueName="location">
        {(props) => <LocationSelect {...props} placeholder="Location" allowNew={true} component={FixedDropdown} />}
      </BulkActionEditControl>

      <BulkActionEditControl
        label="Security Role"
        actionName="securityRoleAction"
        valueName="securityRole"
        allowClear={false}>
        {(props) => (
          <SecurityRoleSelect
            name={props.name}
            value={props.value?.id}
            onChange={(e) => props.onChange({ target: { value: e.target.value ? { id: e.target.value } : null } })}
            disabled={props.disabled}
            placeholder="Security Role"
            component={FixedSingleSelect}
          />
        )}
      </BulkActionEditControl>
    </Form>
  );
}

export default function BulkEditMember({ ids, onClose, onComplete }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();
  const formRef = useRef();

  return (
    <BulkActionModal
      title="Bulk Edit"
      onClose={onClose}
      onComplete={onComplete}
      onConfirm={() => formRef.current?.submitForm()}
      renderRow={MemberDetails}
      width="56rem">
      <Formik
        innerRef={formRef}
        initialValues={{
          jobTitleAction: NO_CHANGE,
          jobTitle: null,
          memberLevelAction: NO_CHANGE,
          memberLevel: null,
          practiceAction: NO_CHANGE,
          practice: null,
          disciplineAction: NO_CHANGE,
          discipline: null,
          managerAction: NO_CHANGE,
          manager: null,
          locationAction: NO_CHANGE,
          location: null,
          holidayScheduleAction: NO_CHANGE,
          holidaySchedule: null,
          securityRoleAction: NO_CHANGE,
          securityRole: null,
          tagsAction: NO_CHANGE,
          tags: [],
          skillsAction: NO_CHANGE,
          skills: [],
          certificationsAction: NO_CHANGE,
          certifications: [],
          loginEnabledAction: NO_CHANGE,
          loginEnabled: null,
          memberTypeAction: NO_CHANGE,
          memberType: null,
        }}
        onSubmit={async (values) => {
          const payload = {};

          const applyField = (key, action, value, map = (v) => v?.id) => {
            if (action === CLEAR) payload[key] = null;
            else if (action === SET && value != null) payload[key] = map(value);
          };

          applyField('jobTitleId', values.jobTitleAction, values.jobTitle);
          applyField('memberLevelId', values.memberLevelAction, values.memberLevel);
          applyField('practiceId', values.practiceAction, values.practice);
          applyField('disciplineId', values.disciplineAction, values.discipline);
          applyField('managerId', values.managerAction, values.manager);
          applyField('locationId', values.locationAction, values.location);
          applyField('holidayScheduleId', values.holidayScheduleAction, values.holidaySchedule);
          applyField('securityRoleId', values.securityRoleAction, values.securityRole);

          applyField('tags', values.tagsAction, values.tags, (v) => v.map((t) => t.id));
          applyField('skills', values.skillsAction, values.skills, (v) => v.map((s) => s.id));
          applyField('certifications', values.certificationsAction, values.certifications, (v) => v.map((c) => c.id));

          applyField('loginEnabled', values.loginEnabledAction, values.loginEnabled, (v) => v === 'true');
          applyField('employmentTypeId', values.memberTypeAction, values.memberType, (v) => v);

          return api.www
            .workspaces(workspace.id)
            .members()
            .bulkEdit(ids, payload)
            .then((r) => r.data);
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
        validate={validate}>
        <BulkEditForm features={features} />
      </Formik>
    </BulkActionModal>
  );
}
